import AsyncAutocomplete from '@components/v2/form/AsyncAutocomplete/AsyncAutocomplete';
import MoneyField from '@components/v2/form/MoneyField/MoneyField';
import { Stack } from '@mui/material';
import { Control, Controller } from 'react-hook-form';
import LangEnum from '../../../../../../../../../lang/LangEnum';
import { ListCustomerAddressDTO } from '../../../../../../../../../types/address.types';
import { CustomerAddress } from '../../../../../../../../customer/types/customer.types';
import { useTranslation } from '../../../../../../../../translation/TranslationContext';
import { OrderCondition } from '../../../../../../../conditions/interfaces/orderCondition.interface';
import { getAddressOptionLabel } from '../../../../../../../conditions/orderCondition.utils';
import { OrderConditionFieldWithError } from '../../../../../../../conditions/orderConditionFieldErrors.map';
import { OrderConditionShippingVisibility } from '../../../orderObjectConditionsTab.types';
import { blurAutocompleteOnPointerSelect, hasFieldError } from '../../orderConditionDetails.utils';
import { OrderConditionDetailsFormValues } from '../orderConditionDetailsForm.schema';

interface OrderConditionDeliveryFieldsProps {
  condition: OrderCondition;
  customerId?: string;
  control: Control<OrderConditionDetailsFormValues>;
  canEditOrder: boolean;
  fieldsWithError: Set<OrderConditionFieldWithError>;
  visibility: OrderConditionShippingVisibility;
  onSave: () => void;
}

const OrderConditionDeliveryFields = ({
  condition,
  customerId,
  control,
  canEditOrder,
  fieldsWithError,
  visibility,
  onSave,
}: OrderConditionDeliveryFieldsProps) => {
  const { translate } = useTranslation();
  const { isShippingCostVisible } = visibility;

  return (
    <Stack sx={{ gap: 2 }}>
      <Controller
        name="deliveryAddressId"
        control={control}
        render={({ field, fieldState }) => (
          <AsyncAutocomplete<CustomerAddress>
            source={{
              url: `/customers/${customerId}/address`,
              limit: 20,
              enabled: !!customerId,
            }}
            label={translate(LangEnum.ORDER_CONDITIONS_DELIVERY_ADDRESS_LABEL)}
            value={field.value ?? null}
            getOptionId={(option) => option.address?.id ?? option.id}
            getOptionLabel={(option) =>
              getAddressOptionLabel(option as unknown as ListCustomerAddressDTO)
            }
            initialData={
              condition.deliveryAddress
                ? ([
                    {
                      id: condition.deliveryAddress.id,
                      address: condition.deliveryAddress,
                      principal: false,
                    },
                  ] as unknown as CustomerAddress[])
                : []
            }
            disabled={!canEditOrder || !customerId}
            error={hasFieldError(fieldState, fieldsWithError, 'deliveryAddressId')}
            helperText={fieldState.error?.message}
            onBlur={field.onBlur}
            onClose={blurAutocompleteOnPointerSelect}
            onChange={(value) => {
              field.onChange(value);
              onSave();
            }}
          />
        )}
      />

      {isShippingCostVisible && (
        <Controller
          name="shippingCostValue"
          control={control}
          render={({ field, fieldState }) => (
            <MoneyField
              label={translate(LangEnum.GLOBAL_SHIPPING_COST)}
              disabled={!canEditOrder}
              value={field.value}
              onChange={field.onChange}
              error={!!fieldState.error}
              helperText={fieldState.error?.message}
              onBlur={() => onSave()}
            />
          )}
        />
      )}
    </Stack>
  );
};

export default OrderConditionDeliveryFields;
